import { useEffect, useState, useMemo, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useUnreadCounts } from '../hooks/useBillRealtime';
import {
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Home as HomeIcon,
  FolderOpen,
  FileText,
  Map,
  Sparkles,
} from 'lucide-react';
import { useAsk } from '../lib/ask';
import FeedbackCard from './FeedbackCard';
import SidebarReferral from './SidebarReferral';
import type { Bill } from '../types/bill';
import '@styles/pages/dashboard.css';
import '@styles/components/app-shell.css';

type Profile = {
  first_name: string | null;
  last_name: string | null;
};

/* Pages that live inside Settings. The Settings nav item stays lit on all
   of them, the same way Cases stays lit inside a case. */
const SETTINGS_PATHS = ['/settings', '/account', '/plan', '/family-members', '/contact'];

type NavItem = { path: string; Icon: typeof HomeIcon; label: string };

/* Oct 2026 revamp, after looking at Granted's portal: three places to go,
   Home, Cases and Settings. Family, Plan, Account and Contact moved into
   Settings so the sidebar stops being a list of everything we have. */
const NAV_ITEMS: NavItem[] = [
  { path: '/', Icon: HomeIcon, label: 'Home' },
  { path: '/bills', Icon: FolderOpen, label: 'Cases' },
  { path: '/settings', Icon: Settings, label: 'Settings' },
];

/* EOB-only customers get the review flow instead of case surfaces. */
const EOB_NAV_ITEMS: NavItem[] = [
  { path: '/eob', Icon: FileText, label: 'EOB Review' },
  { path: '/settings', Icon: Settings, label: 'Settings' },
];

/* Personalized Action Plan customers: their plan is the whole product. */
const ACTION_PLAN_NAV_ITEMS: NavItem[] = [
  { path: '/action-plan', Icon: Map, label: 'My plan' },
  { path: '/settings', Icon: Settings, label: 'Settings' },
];

function navItemsFor(plan: string | null) {
  if (plan === 'eob') return EOB_NAV_ITEMS;
  if (plan === 'action_plan') return ACTION_PLAN_NAV_ITEMS;
  return NAV_ITEMS;
}

/* ============================================
   APP SIDEBAR LAYOUT
   Light, quiet chrome: logo, an "Ask Agent Loop"
   pill that opens chat with the team, three nav
   items, and who's signed in at the bottom.
   Starting a case lives on Home and Cases now,
   not in the sidebar.
============================================ */
export default function AppSidebar({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const ask = useAsk();

  const [bills, setBills] = useState<Bill[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });

  /* ── Load bills + profile ── */
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      try {
        const [billsRes, profileRes, subRes] = await Promise.all([
          supabase
            .from('bills')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('subscriptions')
            .select('plan')
            .eq('user_id', user.id)
            .maybeSingle(),
        ]);

        if (billsRes.data) setBills(billsRes.data);
        if (profileRes.data) setProfile(profileRes.data);
        if (subRes.data) setPlan(subRes.data.plan);
      } catch (err) {
        console.warn('[sidebar] load failed', err);
      }
    };

    load();
  }, [user]);

  /* ── Unread counts ── */
  const billIds = useMemo(() => bills.map((b) => b.id), [bills]);
  const { unreadCounts } = useUnreadCounts(billIds);

  /* One rolled-up unread signal on the Cases nav item. */
  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((a, b) => a + b, 0),
    [unreadCounts],
  );

  const firstName = profile?.first_name
    ? profile.first_name.charAt(0).toUpperCase() + profile.first_name.slice(1)
    : '';
  const initial = (firstName || user?.email || '?').charAt(0).toUpperCase();

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { /* noop */ }
  };

  const path = location.pathname;
  const isActive = (p: string) => {
    if (p === '/bills') return path === '/bills' || path.startsWith('/case/');
    if (p === '/settings') return SETTINGS_PATHS.includes(path) || path.startsWith('/hipaa/family');
    return path === p;
  };

  const items = navItemsFor(plan);

  /* What the + in the middle of the phone tab bar starts. */
  const newThing =
    plan === 'action_plan'
      ? { to: '/action-plan?new=1', label: 'Another bill' }
      : plan === 'eob'
        ? { to: '/plan', label: 'Upgrade plan' }
        : { to: '/add-bill', label: 'New case' };

  return (
    <div className="dashboard-container ml-shell">
      <div className="chat-layout">
        {/* ── SIDEBAR ── */}
        <aside className={`chat-sidebar ml-side ${collapsed ? 'collapsed' : ''}`}>
          {/* Collapsed, the fox on the Ask button is the only mark. Showing it
              twice, once as the logo, looked like a mistake. */}
          {!collapsed && (
            <div className="ml-side-top">
              <button
                type="button"
                className="ml-side-logo"
                onClick={() => navigate('/')}
                aria-label="mediloop home"
              >
                <img src="/images/logos/tail-logo.png" alt="mediloop" />
              </button>
            </div>
          )}

          <button
            type="button"
            className={`ml-ask ${path === '/ask' ? 'active' : ''}`}
            onClick={() => ask.open()}
            title="Ask Agent Loop"
          >
            <span className="ml-ask-icon" aria-hidden>
              <Sparkles size={13} strokeWidth={2.2} />
            </span>
            {!collapsed && <span>Ask Agent Loop</span>}
          </button>

          <nav className="ml-nav" aria-label="Main">
            {items.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`ml-nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : undefined}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                <item.Icon size={17} strokeWidth={1.75} className="ml-nav-icon" />
                {!collapsed && <span>{item.label}</span>}
                {item.path === '/bills' && totalUnread > 0 && (
                  <span
                    className="ml-nav-dot"
                    aria-label={`${totalUnread} unread`}
                    title={`${totalUnread} unread`}
                  />
                )}
              </button>
            ))}
          </nav>

          <div className="ml-side-spacer" aria-hidden />

          <SidebarReferral collapsed={collapsed} />

          <div className="ml-side-foot">
            {!collapsed && (
              <button
                type="button"
                className="ml-me"
                onClick={() => navigate('/account')}
                title="Your account"
              >
                <span className="ml-me-avatar" aria-hidden>{initial}</span>
                <span className="ml-me-text">
                  <span className="ml-me-name">{firstName || 'Your account'}</span>
                  {user?.email && <span className="ml-me-email">{user.email}</span>}
                </span>
              </button>
            )}
            <button
              type="button"
              className="ml-side-toggle"
              onClick={toggleCollapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
        </aside>

        {/* ── PAGE CONTENT ── */}
        <main className="chat-main">
          <div className="sidebar-page-content">
            {children}
          </div>
        </main>
      </div>

      {/* The corner "how was that?" card. Renders nothing unless a page
          asked for it (see FeedbackCard.tsx). */}
      <FeedbackCard />

      {/* ── PHONE TAB BAR (hidden on desktop) ── */}
      <nav className="mobile-bottom-nav ml-tabbar" aria-label="Main">
        {items.slice(0, 1).map((item) => (
          <button
            key={item.path}
            type="button"
            className={`ml-tab ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <item.Icon size={20} strokeWidth={1.75} />
            {item.label}
          </button>
        ))}
        {items.length === 3 && (
          <button
            type="button"
            className={`ml-tab ${isActive('/bills') ? 'active' : ''}`}
            onClick={() => navigate('/bills')}
          >
            <FolderOpen size={20} strokeWidth={1.75} />
            Cases
            {totalUnread > 0 && <span className="ml-tab-dot" aria-hidden />}
          </button>
        )}
        <button
          type="button"
          className="ml-tab-add"
          onClick={() => navigate(newThing.to)}
          aria-label={newThing.label}
          title={newThing.label}
        >
          <Plus size={22} />
        </button>
        <button
          type="button"
          className={`ml-tab ${isActive('/settings') ? 'active' : ''}`}
          onClick={() => navigate('/settings')}
        >
          <Settings size={20} strokeWidth={1.75} />
          Settings
        </button>
      </nav>
    </div>
  );
}
